import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { I18nService } from '@app/core/i18n/i18n.service';
import { Center, IdDisplayName, ResponsiveImageResource, UserProfile } from '@app/core/models';
import { ActiveSubscription } from '@app/pages/billing-methods/billing-methods.interface';
import { DrupalConstants, UserService, ViewService } from '@makiwin/ngx-drupal8-rest';
import {
  DrupalConstants as Drupal7Constants,
  ViewOptions as View7Options,
  ViewService as View7Service,
} from 'ngx-drupal7-services';
import { BehaviorSubject, Observable, catchError, map, mergeMap, of } from 'rxjs';
import { InternetConnectionService } from '../internet-connection/internet-connection.service';
import { LocalStorageService } from '../localstorage/localstorage.service';
import { StorageDocumentsService } from '../storage-documents/storage-documents.service';

import { RolesService } from '../roles/roles.service';
import { GoogleMapService } from '../google/google-map.service';

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  /**
   * Observable of the current user profile.
   */
  currentUserProfile$: Observable<UserProfile | null>;
  /**
   * Signal of the user profile picture.
   */
  profilePic = signal<string | undefined | ResponsiveImageResource>(undefined);
  /**
   * Signal of the current school, if the user is a school user.
   */
  currentSchool = signal<Center | undefined>(undefined);

  d7DataLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  d7ProfileError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private pCurrentUserProfile$: BehaviorSubject<UserProfile | null> = new BehaviorSubject<UserProfile | null>(null);
  private pCurrentUserProfile?: UserProfile;
  private viewService: ViewService = inject(ViewService);
  private userService: UserService = inject(UserService);
  private httpClient: HttpClient = inject(HttpClient);
  private view7Service: View7Service = inject(View7Service);
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  private storageDocumentsService: StorageDocumentsService = inject(StorageDocumentsService);
  private i18nService: any = inject(I18nService);
  private internetConnectionService: any = inject(InternetConnectionService);
  private roleService: RolesService = inject(RolesService);
  private googleMapService: GoogleMapService = inject(GoogleMapService);
  private localProfilePic?: string;
  private readonly defaultImageProfile = '/assets/images/blank-profile.png';

  constructor() {
    this.currentUserProfile$ = this.pCurrentUserProfile$.asObservable();
    this.internetConnectionService.isOnline$.subscribe(() => {
      this.checkProfilePic();
    });
  }

  /**
   * Returns true if the user is logged in.
   */
  get isLogged(): boolean {
    return DrupalConstants.Connection && DrupalConstants.Connection.current_user.uid !== 0;
  }

  /**
   * Returns the current user profile.
   */
  get currentUserProfile(): UserProfile | undefined {
    return this.pCurrentUserProfile;
  }

  /**
   * Set the current user profile.
   */
  set currentUserProfile(userProfile: UserProfile | undefined) {
    if (userProfile?.field_gps_position_export && typeof userProfile?.field_gps_position_export === 'string') {
      try {
        const jsonLatLon = JSON.parse(userProfile.field_gps_position_export.replace(/&quot;/g, '"'));
        if (jsonLatLon.coordinates) {
          userProfile.field_gps_position = { lat: jsonLatLon.coordinates[1], lon: jsonLatLon.coordinates[0] };
        } else {
          userProfile.field_gps_position = undefined;
        }
      } catch (err) {
        console.log('Error parsing user coordinaates', err);
        userProfile.field_gps_position = undefined;
      }
    }
    this.pCurrentUserProfile = userProfile;
    this.roleService.currentTopQualification = userProfile?.top_qualification;
    this.notifyUserProfileChanged();
  }

  drupal7Connect(): Observable<UserProfile> {
    this.d7DataLoading$.next(true);
    return this.httpClient
      .get(`${Drupal7Constants.backEndUrl}services/session/token`, {
        withCredentials: true,
        responseType: 'text',
      })
      .pipe(
        mergeMap((token) =>
          this.httpClient
            .post(
              `${Drupal7Constants.restUrl}system/connect`,
              {},
              {
                withCredentials: true,
                responseType: 'json',
                /* eslint-disable */
                headers: {
                  'X-CSRF-Token': token,
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                /* eslint-enable */
              }
            )
            .pipe(
              mergeMap((connection: any) => {
                connection.token = token;
                Drupal7Constants.Connection = connection;

                const options: View7Options = {
                  display_id: 'profile',
                };
                return this.view7Service.getView('api_current_user_info', options).pipe(
                  map(() => {
                    if (!this.currentUserProfile) {
                      return;
                    }
                    // this.currentUserProfile.field_subscription_newsletter = {
                    //   subscribe: data7[0].custom_field_iko_newsletter_subscriptio,
                    // };
                    // this.currentUserProfile.field_subscription_deals = {
                    //   subscribe: data7[0].custom_field_iko_member_deals,
                    // };
                    // this.currentUserProfile.field_iko_training_alert = {
                    //   subscribe: data7[0].custom_training_alert_subscribe,
                    // };
                    // this.currentUserProfile.field_subscription_standards = {
                    //   subscribe: data7[0].custom_iko_standards_quality_subscribe,
                    // };
                    // // this.currentUserProfile.top_qualification = data7[0].top_qualification;
                    // if (data7[0].field_userexpiry_date) {
                    //   const elem = document.createElement('div');
                    //   elem.innerHTML = data7[0].field_userexpiry_date;
                    //   // WEB-3219 - extract date from the content attribute.
                    //   if (elem.children.length && elem.children[0]?.getAttribute('content')) {
                    //     this.currentUserProfile.subscription_expiry = new Date(
                    //       (elem.children[0]?.getAttribute('content') as string)?.split('+')[0]
                    //     );
                    //   }
                    // }
                    // /* eslint-disable */
                    // const mapping: { [key: string]: number } = {
                    //   Kiteboarder: 75,
                    //   'Kiteboarder Level 1A': 75,
                    //   'Kiteboarder Level 1B': 75,
                    //   'Kiteboarder Level 1C': 75,
                    //   'Kiteboarder Level 1D': 75,
                    //   'Kiteboarder Level 1E': 75,
                    //   'Kiteboarder Level 1': 75,
                    //   'Kiteboarder Level 2F': 76,
                    //   'Kiteboarder 2F': 76,
                    //   'Level 2 F': 76,
                    //   'Kiteboarder Level 2G': 76,
                    //   'Kiteboarder level 2G': 76,
                    //   'Kiteboarder level 2 G': 76,
                    //   'Kiteboarder Level 2H': 76,
                    //   'Kiteboarder level 2H ': 76,
                    //   'Kiteboarder Level 2 H': 76,
                    //   'Kiteboarder Level 2': 76,
                    //   'Level 2 H': 76,
                    //   'Kiteboarder Level 2I': 76,
                    //   'Kiteboarder level 2I': 76,
                    //   'Level 2 I': 76,
                    //   'Kiteboarder Level 3J': 77,
                    //   'Kiteboarder level 3J': 77,
                    //   'Kiteboarder Level 3K': 77,
                    //   'Kiteboarder level 3K': 77,
                    //   'Level 3K': 77,
                    //   'Kiteboarder Level 3L': 77,
                    //   'Kiteboarder level 3L': 77,
                    //   'Level 3L': 77,
                    //   'Kiteboarder Level 3M': 77,
                    //   'Kiteboarder level 3M': 77,
                    //   'Level-3m': 77,
                    //   'Kiteboarder Level 3N': 77,
                    //   'Kiteboarder level 3N': 77,
                    //   'Kiteboarder level 3 N': 77,
                    //   'Kiteboarder level 3': 77,
                    //   'Kiteboarder 3N': 77,
                    //   'Kiteboarding Level 3N': 77,
                    //   'Kiteboarder Level 4O': 78,
                    //   'Kiteboarder Level 4P': 78,
                    //   'Kiteboarder Level 4Q': 78,
                    //   'Kiteboarder Level 4R': 78,
                    //   'Kiteboarder Level 4S': 78,
                    //   'Kiteboarder Level 4T': 78,
                    //   'Kiteboarder Level 4U': 78,
                    //   'Kiteboarder Level 4V': 78,
                    //   'Kiteboarder Level 4W': 78,
                    //   'Kiteboarder Level 4X': 78,
                    //   'Kiteboarder Level 4Y': 78,
                    //   'Kiteboarder Level 4': 78,
                    //   '1a': 75,
                    //   '1A': 75,
                    //   '1b': 75,
                    //   '1B': 75,
                    //   '1c': 75,
                    //   '1C': 75,
                    //   '1d': 75,
                    //   '1D': 75,
                    //   '1e': 75,
                    //   '1E': 75,
                    //   '2f': 76,
                    //   '2F': 76,
                    //   '2g': 76,
                    //   '2G': 76,
                    //   '2h': 76,
                    //   '2H': 76,
                    //   '2i': 76,
                    //   '2I': 76,
                    //   '3j': 77,
                    //   '3J': 77,
                    //   '3k': 77,
                    //   '3K': 77,
                    //   '3 K': 77,
                    //   '3l': 77,
                    //   '3L': 77,
                    //   '3m': 77,
                    //   '3M': 77,
                    //   '3n': 77,
                    //   '3N': 77,
                    //   '4o': 78,
                    //   '4O': 78,
                    //   'Kiteboarder Level 4.1': 78,
                    //   '4p': 78,
                    //   '4P': 78,
                    //   'Kiteboarder Level 4.2': 78,
                    //   '4q': 78,
                    //   '4Q': 78,
                    //   'Kiteb78er Level 4.3': 78,
                    //   '4r': 78,
                    //   '4R': 78,
                    //   'Kiteboarder Level 4.4': 78,
                    //   '4s': 78,
                    //   '4S': 78,
                    //   'Kiteboarder Level 4.5': 78,
                    //   '4t': 78,
                    //   '4T': 78,
                    //   'Kiteboarder Level 4.6': 78,
                    //   '4u': 78,
                    //   '4U': 78,
                    //   'Kiteboarder Level 4.7': 78,
                    //   '4v': 78,
                    //   '4V': 78,
                    //   'Kiteboarder Level 4.8': 78,
                    //   '4w': 78,
                    //   '4W': 78,
                    //   'Kiteb78er Level 4.9': 78,
                    //   '4x': 78,
                    //   '4y': 78,
                    //   '4Y': 78,
                    //   'Kiteboarder Level 4:Jump With grab': 78,
                    //   'Kiteboarder Level 4:Jibe': 78,
                    //   'Kiteboarder Level 4:Board recovery': 78,
                    //   'Kiteboarder Level 4:PRE ITC courses': 78,
                    //   'Kiteboarder Level 4:Kite theory': 78,
                    //   'Instructor Level 1': 50,
                    //   'Instructor Level 2': 51,
                    //   'Istruttore di Livello 2': 51,
                    //   'Instructor Level 2 Senior - Water': 51,
                    //   'Esame per Istruttore IKO di livello 2 e Responsabile di Tirocinio': 51,
                    //   'Instructor Level 2 and Internship Manager Exam': 51,
                    //   'Examen de Instructor de Nivel 2 y Director de Prácticas': 51,
                    //   'Moniteur Niveau 2': 51,
                    //   'Examen de Moniteur de Niveau 2 et de Responsable de Stage': 51,
                    //   'Exame do Instructor Nível 2 e do Gerente de Estágio': 51,
                    //   'Instructor Level 2 und Internship Manager Examen': 51,
                    //   'Instrutor de nivel 2': 51,
                    //   'Instructor Nivel 2': 51,
                    //   'Instructor Equivalency': 51,
                    //   'Instructor Level 3': 52,
                    //   'Moniteur Niveau 3': 52,
                    //   'Istruttore di Livello 3': 52,
                    //   'Instructor Level 3 Exam': 52,
                    //   'Instructor Nivel 3': 52,
                    //   'Instrutor de nivel 3': 52,
                    //   'Examen del Instructor Nivel 3': 52,
                    //   'Examen du Moniteur Niveau 3': 52,
                    //   'Esame dell’Istruttore di Livello 3': 52,
                    //   Coach: 133,
                    //   'Assistant Trainer': 82,
                    //   'Assistant Trainer / Coach': 82,
                    //   'Assistant Instructor Trainer': 82,
                    //   'Coach Trainer': 81,
                    //   'Assistant Trainer / Coach Trainer': 81,
                    //   // L5 Freestyle.
                    //   Freestyle: 165,
                    //   'Back roll with a grab': 165,
                    //   'Back-roll and kite-loop': 165,
                    //   'Front roll both directions': 165,
                    //   'High back-roll': 165,
                    //   'Higher jumps with grab and smooth landings': 165,
                    //   'Higher jumps with smooth landings': 165,
                    //   'Hooked-in railey': 165,
                    //   'Hooked-in railey with grab': 165,
                    //   'Jump and kite-loop': 165,
                    //   'Jump and land with a down-loop': 165,
                    //   'Surface handle pass (flat water without fins)': 165,
                    //   'Unhooked back-roll': 165,
                    //   'Unhooked jump': 165,
                    //   'Unhooked jump with grab': 165,
                    //   'Unhooked railey': 165,
                    //   // L5 Freeride.
                    //   Freeride: 164,
                    //   'Back-roll both directions': 164,
                    //   'Basic back-roll': 164,
                    //   'Broad reach down-loop (both side)': 164,
                    //   'Jump without kite input (pop)': 164,
                    //   'Jump without kite input (pop)-hr': 164,
                    //   'Kite-loop the kite, broad reach riding': 164,
                    //   'Long distance downwinder': 164,
                    //   'Power turn (jibe + down-loop)': 164,
                    //   'Power turn both way': 164,
                    //   // L5 Wave.
                    //   Wave: 166,
                    //   'Getting off the water with shore break': 166,
                    //   Jibe: 166,
                    //   'Jumping on a wave-sr': 166,
                    //   'Riding on waves over 1m (all previous skills)-sr': 166,
                    //   // L5 Hydrofoil.
                    //   Hydrofoil: 167,
                    //   'Controlled Stops and Basic Turns': 167,
                    //   'Do a downwinder in waves': 167,
                    //   'Do a downwinder on waves': 167,
                    //   'S turns on flat water': 167,
                    //   'Water start': 167,
                    //   Assistant: 54,
                    //   'Assistant Instructor': 54,
                    //   'Assistant instructor': 54,
                    //   'Assistant Instructor 3 Days': 54,
                    //   // 'Assistant Instructor 3 Days => Assistant': 54,
                    //   // 'Assistant => Assistant': 54,
                    //   // By course ID.
                    //   '200505': 5,
                    //   '398': 5,
                    //   '893': 2,
                    //   '503': 39,
                    //   '1413': 39,
                    //   '1409': 4,
                    //   '192315': 34,
                    //   '196622': 36,
                    //   '228847': 35,
                    //   Examiner: 80,
                    //   'Examiner / Coach Trainer': 80,
                    //   'Examiner / Coach': 80,
                    //   'Coach Level 1': 468,
                    //   'Coach Level 2': 133,
                    //   'Coach Level 1 - Wing Foil': 434370,
                    // };
                    // /* eslint-enable */
                    // // TODO: disable when d8 ready
                    // this.currentUserProfile.d8_qualification_id =
                    //   mapping[this.currentUserProfile.top_qualification ?? 'Kitboarder'];
                    // if (!this.currentUserProfile.d8_qualification_id) {
                    //   this.currentUserProfile.d8_qualification_id = 75;
                    // }

                    // // this.currentUserProfile.field_training_level_skills = [
                    // //   {
                    // //     category_id: this.currentUserProfile.d8_qualification_id,
                    // //   } as any,
                    // // ];

                    // // if (redirect) {
                    // //   this.navigate('/');
                    // // }
                    // this.notifyUserProfileChanged();
                    this.d7DataLoading$.next(false);
                  })
                ) as Observable<any>;
              })
            )
        )
      );
  }

  /**
   * Init the current user profile.
   *
   * @param notSetLanguage
   * Not set the language of the app according to the user profile.
   * @returns
   * An observable of the current user profile.
   */
  initProfile(notSetLanguage?: boolean): Observable<UserProfile | null> {
    return this.viewService.get('/system/connect').pipe(
      mergeMap((connection: any) => {
        if (!connection || connection.current_user.uid === 0) {
          // empty current session
          return this.userService.attemptToLoginWithSavedCredentials().pipe(
            mergeMap(() => this.initProfile(notSetLanguage)),
            catchError(() => this.deleteCurrentSession())
          );
        }
        // let redirect = false;
        // if (!DrupalConstants.Connection) {
        //   redirect = true;
        // }
        // set the current session
        DrupalConstants.Connection = connection;
        DrupalConstants.Token = connection.csrf_token;
        // save the connection in localstorage
        this.localStorageService.setItem('connection', JSON.stringify(connection));
        // get current time in ms
        const now = new Date().getTime();
        // get the future expiration time in ms
        const expiration = now + (DrupalConstants.Settings.cookieLifetime as any) * 1000;
        // set the expiration time
        this.localStorageService.setItem('expiration', expiration.toString());
        this.localStorageService.setItem('token', connection.csrf_token);
        return this.viewService.get('/api/v1/user_profile.json').pipe(
          mergeMap((profileData: UserProfile[]) => {
            if (!profileData[0]) {
              // this.navigate('/');
              return of(null);
            }
            // WEB-5322
            // Fetch the most recent active subscription and update the fields
            return this.viewService.get('/api/v1/user-active-subscription.json').pipe(
              mergeMap((activeSubscriptions: ActiveSubscription[]) => {
                let expiredRequest: Observable<any[]> = of([]);
                if (activeSubscriptions[0]) {
                  profileData[0].next_renewal = activeSubscriptions[0].next_renewal;
                } else if (profileData[0].current_role.id === 'member_expired' && !profileData[0].subscription_expiry) {
                  // If user is expired and the backend doesn't have the expiry date:
                  // try fetching the expiry date from last license
                  expiredRequest = this.viewService.get('/api/v1/my_expired_license.json');
                }
                this.drupal7Connect().subscribe();
                return expiredRequest.pipe(
                  mergeMap((licenseData) => {
                    const licenseExpiration = new Date(licenseData[0]?.expires);
                    // The backend returns 1969-12-31 when the expiration is set to unlimited
                    // so we ignore the value in that case
                    if (licenseExpiration.getFullYear() > 1970) {
                      profileData[0].subscription_expiry = licenseData[0]?.expires;
                    }
                    if (!profileData[0].user_picture_multiple_json) {
                      profileData[0].user_picture_multiple_json = '/assets/images/blank-profile.png' as any;
                    }
                    if (profileData[0].field_previous_role) {
                      profileData[0].previous_role = this.extractPreviousRole(profileData[0].field_previous_role);
                    }
                    const currentUserProfile = profileData[0];
                    if (currentUserProfile.field_phone && currentUserProfile.field_phone.indexOf('+') === -1) {
                      currentUserProfile.field_phone = `+${currentUserProfile.field_phone}`;
                    }
                    currentUserProfile.d7_expiry_date = currentUserProfile.d7_expiry_date
                      ?.replace(/<.*?>/g, '')
                      .replace('\n', '');
                    // currentUserProfile.top_qualification = currentUserProfile.field_top_qualification;
                    currentUserProfile.has_map_location = profileData[0].has_map_location === '1';
                    if (
                      currentUserProfile.field_training_level_skills &&
                      currentUserProfile.field_training_level_skills.length > 0
                    ) {
                      const qualification: any = currentUserProfile.field_training_level_skills.pop();
                      const qualificationLabel = qualification?.label;
                      if (!currentUserProfile.top_qualification) {
                        currentUserProfile.top_qualification = qualificationLabel;
                      }
                      currentUserProfile.field_training_level_skills.push(qualification);
                      if (currentUserProfile.top_qualification?.toLocaleLowerCase().indexOf('level 5') !== -1) {
                        const levels5 = currentUserProfile.field_training_level_skills
                          .filter((level) => level.label.toLocaleLowerCase().indexOf('level 5') !== -1)
                          .map((level) => level.label);
                        if (levels5.length > 1) {
                          currentUserProfile.top_qualification = levels5.join('<br/>');
                        }
                      }
                    }
                    if (profileData[0].preferred_langcode && !notSetLanguage) {
                      const language = profileData[0].preferred_langcode?.code.toLowerCase();
                      if (this.i18nService.language !== language) {
                        this.googleMapService.skipReloadOnLanguageChange = true;
                        this.i18nService.language = language;
                        setTimeout(() => (this.googleMapService.skipReloadOnLanguageChange = false), 1000);
                      }
                    }

                    if (currentUserProfile.school) {
                      return this.loadSchoolDetails(currentUserProfile);
                    }

                    // Re init user roles
                    // We need to reinit the roles before setting currentuserprofile
                    // as it will emits the notifyUserProfileChanged, the components that
                    // uses the roles service will fail if we inited the roles after the profile assign
                    this.roleService.init(currentUserProfile);

                    this.currentUserProfile = currentUserProfile;

                    // converting imgUrl to base64 and store in service var so user loose the connection
                    // after upload pic but he/she download vCard with image
                    this.saveLocaleProfilePic();
                    return of(this.currentUserProfile);
                  })
                );
              })
            );
          })
        );
      }),
      // prevent to give error and block the boostrap of application
      // so initcordova can be executed and hide the splashscreen
      catchError((err) => {
        console.log('Error init profile', err);
        return of(null);
      })
    );
  }

  /**
   * Notify the current user profile has changed.
   */
  notifyUserProfileChanged(): void {
    this.pCurrentUserProfile$.next(this.pCurrentUserProfile as any);
    this.checkProfilePic();
  }

  /**
   * Reload the current user profile.
   */
  reloadProfile(): void {
    this.initProfile().subscribe();
  }

  /**
   * Logout the current user.
   */
  logoutUser(): void {
    this.pCurrentUserProfile$.next(null);
    this.currentUserProfile = undefined;
    this.currentSchool.set(undefined);
  }

  /**
   * Patch the current user profile, without saving it on backend.
   * @param field
   *  Field to patch.
   * @param value
   *  Value to patch.
   */
  patchUserProfileValue(field: string, value: any) {
    (this.pCurrentUserProfile as any)[field] = value;
    this.notifyUserProfileChanged();
  }

  /**
   * Load school details if the uer is a school user.
   * @param currentUserProfile
   * Current user profile.
   * @returns
   * Observable of the current user profile.
   */
  private loadSchoolDetails(currentUserProfile: UserProfile): Observable<UserProfile> {
    return this.viewService
      .get('/api/v1/school_details.json', {
        args: [currentUserProfile.school?.id?.toString() ?? ''],
      })
      .pipe(
        map((centers) => {
          if (centers?.[0]) {
            this.currentSchool.set(centers[0] as Center);
            currentUserProfile.user_picture_multiple_json = centers[0].field_school_logo as ResponsiveImageResource;
            currentUserProfile.schoolName = centers[0].title;
          }
          this.currentUserProfile = currentUserProfile;
          this.saveLocaleProfilePic();
          return currentUserProfile;
        })
      );
  }

  /**
   * Save the profile picture in local.
   */
  private saveLocaleProfilePic() {
    if (
      this.currentUserProfile?.user_picture_multiple_json &&
      this.currentUserProfile?.user_picture_multiple_json?.square_md
    ) {
      const userPictureMultipleJson = this.currentUserProfile.user_picture_multiple_json.square_md;
      this.storageDocumentsService.getLocalUrl({ fileURL: userPictureMultipleJson }).subscribe((data) => {
        this.localProfilePic = data;
        this.checkProfilePic();
      });
    } else {
      this.localProfilePic = undefined;
    }
    this.checkProfilePic();
  }

  /**
   * Check the profile picture for the current user.
   */
  private checkProfilePic() {
    if (!this.isLogged) {
      return this.profilePic.set(this.defaultImageProfile);
    }
    return this.profilePic.set(
      this.localProfilePic ?? this.currentUserProfile?.user_picture_multiple_json ?? this.defaultImageProfile
    );
  }

  /**
   * Delete the current session.
   * @returns
   * Observable that emits null when the deletion is completed.
   */
  private deleteCurrentSession(): Observable<any> {
    DrupalConstants.Connection = undefined as any;
    DrupalConstants.Token = undefined as any;
    // removed saved data
    document.cookie = 'CHOCOLATECHIPSSL=;path=/;domain=.ikointl.com;expires=Thu, 01 Jan 1970 00:00:01 GMT';
    this.localStorageService.removeItem('connection');
    this.localStorageService.removeItem('expiration');
    this.localStorageService.removeItem('token');
    this.userService.deleteStoredCredentials();
    return of(null);
  }

  private extractPreviousRole(fieldPreviousRole: string): IdDisplayName | undefined {
    const matches = /([a-z0-9 ]+) \(([^)]+)\)/gim.exec(fieldPreviousRole);
    if (!matches) {
      return undefined;
    }
    if (matches.length < 3) {
      return undefined;
    }
    return {
      id: matches[2],
      display_name: matches[1],
    };
  }
}
